import {Component, computed, effect, inject, signal} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {toSignal} from '@angular/core/rxjs-interop';
import {lastValueFrom, map} from 'rxjs';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {injectQuery} from '@tanstack/angular-query-experimental';
import {TranslatePipe} from '@ngx-translate/core';
import {Address, ContactDetails, ContactService, CreateContactRequest, UpdateContactRequest} from '../../../../../api';
import {QueryKey} from '../../../../constants/query-key';
import {Title} from '../../../../components/title/title';
import {AddressFormModel} from '../address-form/address-form.model';
import {ContactFormModel, CreateContactFormValue} from '../contact-form/contact-form.model';
import {DirectoryStore} from '../../services/directory-store';
import {ContactForm} from '../contact-form/contact-form';
import {RouteUri} from '../../../../constants/route-uri';
import {CurrentUserService} from '../../../../services/current-user.service';
import {ElciaCard} from '../../../../components/card/card';
import {SaveBar} from '../../../../components/save-bar/save-bar';
import {HasUnsavedChanges} from '../../../../guards/unsaved-changes.guard';

@Component({
  selector: 'elcia-contact-form-home',
  imports: [
    Title,
    TranslatePipe,
    ContactForm,
    ElciaCard,
    SaveBar
  ],
  templateUrl: './contact-form-home.html',
  styleUrl: './contact-form-home.scss',
})

export class ContactFormHome implements HasUnsavedChanges {

  private readonly route = inject(ActivatedRoute);
  private readonly contactService = inject(ContactService);
  private readonly directoryStore = inject(DirectoryStore);
  private readonly router = inject(Router);
  private readonly currentUserService = inject(CurrentUserService);

  private readonly contactId = toSignal(this.route.params.pipe(map(params => params['id'] ? Number(params['id']) : null)));

  protected readonly isEditMode = computed(() => this.contactId() != null);

  private readonly contactQuery = injectQuery(() => ({
    queryKey: [QueryKey.CONTACT, this.contactId()],
    queryFn: () => lastValueFrom(this.contactService.getContactById(this.contactId()!)),
    enabled: this.contactId() != null,
  }));

  protected readonly contactName = computed(() => {
    const contact = this.contactQuery.data()?.contact;
    if (!contact) return '';
    return [contact.firstName, contact.lastName].filter(Boolean).join(' ');
  });

  protected readonly form = this.createContactForm();

  // form.dirty / form.invalid are not signals: form.events republishes every pristine and status change
  private readonly formEvent = toSignal(this.form.events);
  protected readonly isDirty = computed(() => {
    this.formEvent();
    return this.form.dirty;
  });
  protected readonly isInvalid = computed(() => {
    this.formEvent();
    return this.form.invalid;
  });

  protected readonly isSaving = computed(() =>
    this.directoryStore.createContactMutation.isPending() || this.directoryStore.updateContactMutation.isPending());

  protected readonly savedAt = signal<Date | null>(null);

  // Peuple le form une seule fois par contact chargé : un refetch (déclenché par
  // l'invalidate après sauvegarde) ne doit pas réécraser les éditions en cours via patchValue
  private populatedContactId: number | null = null;
  private readonly populateForm = effect(() => {
    const contact = this.contactQuery.data()?.contact;
    if (!contact) return;
    if (contact.id === this.populatedContactId) return;
    this.populatedContactId = contact.id;
    this.patchFromContact(contact);
  });

  // En création, présélectionne succursale, langue et pays du user connecté
  private readonly presetCreateDefaults = effect(() => {
    if (this.isEditMode()) return;
    this.applyCreateDefaults();
  });

  private patchFromContact(contact: ContactDetails) {
    this.form.patchValue({
      firstname: contact.firstName ?? '',
      lastname: contact.lastName ?? '',
      email: contact.email,
      phone: contact.phone,
      companyId: contact.companyId ?? null,
      legalEntityAssignedId: contact.legalEntityAssignedId ?? null,
      localeIsoCode: contact.localeIsoCode ?? this.currentUserService.currentUser()?.localeIsoCode ?? null,
      note: contact.note ?? '',
      isSameAddressForBilling: contact.isSameAddressForBillingAddress,
      isActive: contact.isActive,
      addressForm: this.mapAddress(contact.address),
      billingAddressForm: this.mapAddress(contact.billingAddress),
    });
  }

  // Ne remplace jamais une saisie manuelle. Rejoué par « Ignorer », qui repart d'un form vierge.
  private applyCreateDefaults() {
    const user = this.currentUserService.currentUser();
    const controls = this.form.controls;

    if (user?.legalEntityId != null && controls.legalEntityAssignedId.value == null) {
      controls.legalEntityAssignedId.setValue(user.legalEntityId);
    }

    if (user?.localeIsoCode != null && controls.localeIsoCode.value == null) {
      controls.localeIsoCode.setValue(user.localeIsoCode);
    }

    const countryIsoCode = this.currentUserService.defaultCountryIsoCode();
    [controls.addressForm.controls.countryIsoCode, controls.billingAddressForm.controls.countryIsoCode]
      .filter(control => !control.dirty)
      .forEach(control => control.setValue(countryIsoCode));
  }

  private mapAddress(address?: Address) {
    return {
      street: address?.street ?? null,
      number: address?.number ?? null,
      postalCode: address?.postalCode ?? null,
      city: address?.city ?? null,
      countryIsoCode: address?.countryIsoCode ?? 'BE',
    };
  }

  hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  // Édition : reste sur la fiche. Création : redirige vers la grille.
  protected submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const contact = this.contactQuery.data()?.contact;

    if (contact) {
      this.directoryStore.updateContactMutation.mutate({
        id: contact.id,
        body: this.mapToUpdateRequest(value, contact),
      }, {
        onSuccess: () => {
          this.form.markAsPristine();
          this.savedAt.set(new Date());
        },
      });
      return;
    }

    this.directoryStore.createContactMutation.mutate(this.mapToCreateRequest(value), {
      onSuccess: () => {
        // Pristine first, otherwise the unsaved-changes guard blocks the redirect
        this.form.markAsPristine();
        this.router.navigate(['/' + RouteUri.DIRECTORY, RouteUri.DIRECTORY_CONTACTS]);
      },
    });
  }

  // « Ignorer » : revient aux valeurs du serveur en édition, à un formulaire vierge en création
  protected discard() {
    const contact = this.contactQuery.data()?.contact;
    if (contact) {
      this.patchFromContact(contact);
    } else {
      this.form.reset();
      this.applyCreateDefaults();
    }

    this.form.markAsPristine();
  }

  //#region Mapper

  private mapToCreateRequest(contact: CreateContactFormValue): CreateContactRequest {
    return {
      firstName: contact.firstname ?? '',
      lastName: contact.lastname ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      isActive: contact.isActive ?? true,
      isSameAddressForBillingAddress: contact.isSameAddressForBilling ?? true,
      address: contact.addressForm,
      billingAddress: contact.billingAddressForm,
      note: contact.note ?? '',
      companyId: contact.companyId,
      legalEntityAssignedId: contact.legalEntityAssignedId,
      localeIsoCode: contact.localeIsoCode
    }
  }

  private mapToUpdateRequest(value: CreateContactFormValue, contact: ContactDetails): UpdateContactRequest {
    return {
      firstName: value.firstname ?? '',
      lastName: value.lastname ?? '',
      email: value.email ?? '',
      phone: value.phone ?? '',
      isActive: value.isActive,
      crmId: contact.crmId,
      accountingAccountName: contact.accountingAccountName,
      isSameAddressForBillingAddress: value.isSameAddressForBilling ?? true,
      address: value.addressForm,
      billingAddress: value.billingAddressForm,
      note: value.note ?? '',
      companyId: value.companyId,
      legalEntityAssignedId: value.legalEntityAssignedId,
      localeIsoCode: value.localeIsoCode
    }
  }

  //#endregion

  //#region Forms Factory

  private createContactForm(): FormGroup<ContactFormModel> {
    return new FormGroup<ContactFormModel>({
      firstname: new FormControl(''),
      lastname: new FormControl(''),
      email: new FormControl(''),
      phone: new FormControl(''),
      companyId: new FormControl<number | null>(null),
      legalEntityAssignedId: new FormControl<number | null>(null),
      localeIsoCode: new FormControl<string | null>(null),
      isSameAddressForBilling: new FormControl<boolean>(true, {nonNullable: true}),
      isActive: new FormControl<boolean>(true, {nonNullable: true}),
      addressForm: this.createAddressForm(),
      billingAddressForm: this.createAddressForm(),
      note: new FormControl('', {nonNullable: true})
    });
  }

  private createAddressForm(): FormGroup<AddressFormModel> {
    return new FormGroup<AddressFormModel>({
      id: new FormControl(0),
      street: new FormControl<string | null>(null),
      number: new FormControl<string | null>(null),
      postalCode: new FormControl<string | null>(null),
      city: new FormControl<string | null>(null),
      countryIsoCode: new FormControl('BE', {nonNullable: true, validators: Validators.required})
    });
  }

  //#endregion

}
